import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, Link, useLocation } from 'react-router-dom';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import Homepage from './pages/Homepage';
import MemoryUploadPage from './pages/MemoryUploadPage';
import CheckoutPage from './pages/CheckoutPage';
import OrderSuccessPage from './pages/OrderSuccessPage';
import LettersLandingPage from './pages/LettersLandingPage';
import LpPrivacyPage from './pages/LpPrivacyPage';
import LpTermsPage from './pages/LpTermsPage';
import LpContactPage from './pages/LpContactPage';
import VaultEditPage from './pages/VaultEditPage';
import VaultViewerPage from './pages/VaultViewerPage';
import MyOrdersPage from './pages/MyOrdersPage';
import VaultPasscodeResetPage from './pages/VaultPasscodeResetPage';
import CartDrawer from './components/CartDrawer';
import { CartProvider } from './context/CartContext';
import { Toaster } from './components/ui/sonner';
import './App.css';

const CANONICAL_ORIGIN = 'https://sent2ment.co.uk';

// Public, indexable routes. Everything else is either noindex or redirected.
const INDEXABLE_ROUTES = new Set([
  '/',
  '/letters-that-last',
  '/memory-vault',
  '/lp-privacy',
  '/lp-terms',
  '/lp-contact',
]);

// Dynamic / private routes that are valid but should not be indexed
const NOINDEX_PREFIXES = [
  '/letters-that-last/checkout',
  '/letters-that-last/success',
  '/letters-that-last/upload/',
  '/upload/',
  '/vault-edit/',
  '/vault/',
  '/vault-passcode-reset/',
  '/my-orders',
  '/manage',
];

const setMetaRobots = (content) => {
  let el = document.querySelector('meta[name="robots"]');
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', 'robots');
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setCanonical = (href) => {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const SeoManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const isIndexable = INDEXABLE_ROUTES.has(pathname);
    const isNoindexPrefix = NOINDEX_PREFIXES.some((p) => pathname.startsWith(p));
    if (isIndexable) {
      setMetaRobots('index, follow');
      setCanonical(`${CANONICAL_ORIGIN}${pathname === '/' ? '/' : pathname}`);
    } else if (isNoindexPrefix) {
      setMetaRobots('noindex, follow');
      setCanonical(`${CANONICAL_ORIGIN}/`);
    } else {
      setMetaRobots('noindex, nofollow');
      setCanonical(`${CANONICAL_ORIGIN}/`);
    }
  }, [pathname]);
  return null;
};

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        const el = document.getElementById(hash.slice(1));
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
};

// Hard redirect for legacy / unknown routes → canonical homepage.
// Sets noindex + uses window.location.replace so the URL changes in the address bar
// and Google clearly sees the destination, not a soft-404 SPA render.
const LegacyRouteRedirect = () => {
  useEffect(() => {
    setMetaRobots('noindex, nofollow');
    window.location.replace(`${CANONICAL_ORIGIN}/`);
  }, []);
  return (
    <div
      data-testid="legacy-route-redirect"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#F7F5F2',
        color: '#0B1F3A',
        fontFamily: 'Manrope, sans-serif',
        fontSize: '0.9rem',
        letterSpacing: '0.02em',
      }}
    >
      Redirecting…
    </div>
  );
};

const MainLayout = () => (
  <>
    <Navigation />
    <Outlet />
    <Footer />
  </>
);

const LtlSuccessLayout = () => (
  <>
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#F7F5F2]/95 backdrop-blur-md border-b border-[#C6A96B]/8">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 flex items-center justify-center h-20">
        <Link to="/" aria-label="Sent2ment home" data-testid="ltl-success-logo-link" className="inline-flex">
          <img
            src="/sent2ment-logo.png"
            alt="Sent2ment"
            className="h-14 w-auto object-contain"
            data-testid="ltl-success-logo"
          />
        </Link>
      </div>
    </nav>
    <div className="pt-20">
      <OrderSuccessPage />
    </div>
  </>
);

const LtlMinimalLayout = ({ children }) => (
  <>
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#F7F5F2]/95 backdrop-blur-md border-b border-[#C6A96B]/8">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <Link to="/" aria-label="Sent2ment home" data-testid="ltl-minimal-logo-link" className="inline-flex">
          <img
            src="/sent2ment-logo.png"
            alt="Sent2ment"
            className="h-14 w-auto object-contain"
            data-testid="ltl-minimal-logo"
          />
        </Link>
        <div className="flex items-center gap-5 sm:gap-6">
          <Link
            to="/"
            data-testid="ltl-minimal-nav-home"
            className="whitespace-nowrap text-[10px] sm:text-xs tracking-[0.18em] sm:tracking-[0.22em] uppercase font-medium transition-opacity duration-300 hover:opacity-80"
            style={{ color: '#C6A96B' }}
          >
            Home
          </Link>
          <Link
            to="/my-orders"
            data-testid="ltl-minimal-nav-my-orders"
            className="whitespace-nowrap text-[10px] sm:text-xs tracking-[0.18em] sm:tracking-[0.22em] uppercase font-medium transition-opacity duration-300 hover:opacity-80"
            style={{ color: '#C6A96B' }}
          >
            My Orders
          </Link>
        </div>
      </div>
    </nav>
    <div className="pt-20">
      {children}
    </div>
  </>
);

function App() {
  return (
    <CartProvider>
      <Router>
        <ScrollToTop />
        <SeoManager />
        <div className="min-h-screen bg-[#F7F5F2] text-[#0B1F3A]">
          <Routes>
            {/* ── Homepage now points back to the main Sent2ment landing page.
                ComingSoonPage is preserved (unused) so the gate can be
                re-enabled later by switching this single line. */}
            <Route path="/" element={<LettersLandingPage />} />
            <Route path="/letters-that-last" element={<LettersLandingPage />} />
            <Route path="/letters-that-last/checkout" element={<LtlMinimalLayout><CheckoutPage /></LtlMinimalLayout>} />
            <Route path="/letters-that-last/success" element={<LtlMinimalLayout><OrderSuccessPage /></LtlMinimalLayout>} />
            <Route path="/letters-that-last/upload/:qr_code" element={<LtlMinimalLayout><MemoryUploadPage /></LtlMinimalLayout>} />
            <Route path="/upload/:qr_code" element={<LtlMinimalLayout><MemoryUploadPage /></LtlMinimalLayout>} />
            <Route path="/order-success" element={<LtlMinimalLayout><OrderSuccessPage /></LtlMinimalLayout>} />
            {/* /memory-vault removed for Letters That Last launch — falls to LegacyRouteRedirect → `/` (noindex). Internal vault routes below remain active for existing customers. */}
            <Route path="/vault-edit/:qr_code" element={<LtlMinimalLayout><VaultEditPage /></LtlMinimalLayout>} />
            <Route path="/vault/:qr_code" element={<LtlMinimalLayout><VaultViewerPage /></LtlMinimalLayout>} />
            <Route path="/vault-passcode-reset/:qr_code" element={<LtlMinimalLayout><VaultPasscodeResetPage /></LtlMinimalLayout>} />
            <Route path="/my-orders" element={<LtlMinimalLayout><MyOrdersPage /></LtlMinimalLayout>} />
            <Route path="/manage" element={<LtlMinimalLayout><MyOrdersPage /></LtlMinimalLayout>} />
            <Route path="/lp-privacy" element={<LpPrivacyPage />} />
            <Route path="/lp-terms" element={<LpTermsPage />} />
            <Route path="/lp-contact" element={<LpContactPage />} />
            <Route path="*" element={<LegacyRouteRedirect />} />
          </Routes>
          <CartDrawer />
          <Toaster />
        </div>
      </Router>
    </CartProvider>
  );
}

export default App;
